// frontend/src/components/FinanceReport/Accounting/components/accounts/suppliers/SupplierAccountDetailPage.jsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAllSupplierPrepayments,
  getAllAllocations,
  deleteSupplierPrepayment,
} from "./requests";
import SupplierDocumentsModal from "./SupplierDocumentsModal";
import SupplierMovementFormModal from "./SupplierMovementFormModal";

const STATUS_LABELS = { PENDING: "Ожидает", PAID: "Получено" };

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("ru-RU");
};

const formatCreatedDay = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("ru-RU");
};

export default function SupplierAccountDetailPage() {
  const { supplierId } = useParams();
  const navigate = useNavigate();

  console.log("🟢 SupplierAccountDetailPage RENDER, supplierId =", supplierId);

  const [items, setItems] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docsItem, setDocsItem] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [txToDelete, setTxToDelete] = useState(null);

  const endRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, allocList] = await Promise.all([
        getAllSupplierPrepayments(supplierId),
        getAllAllocations().catch(() => []),
      ]);
      setItems(list);
      setAllocations(allocList);
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!loading && items.length > 0 && endRef.current) {
      endRef.current.scrollIntoView({ behavior: "auto", block: "end" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const supplierName =
    items[0]?.supplier_name || `Поставщик #${supplierId}`;

  const allocationRecordMap = useMemo(() => {
    const map = {};
    allocations.forEach((a) => {
      map[String(a.id)] = a.accounting_record;
    });
    return map;
  }, [allocations]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const da = a.date || "";
      const db = b.date || "";
      if (da < db) return -1;
      if (da > db) return 1;
      return 0;
    });
  }, [items]);

  const balance = useMemo(() => {
    let bal = 0;
    items.forEach((p) => {
      if (p.payment_status !== "PAID") return;
      const amt = Number(p.amount || 0);
      if (p.type === "income") bal += amt;
      else bal -= amt;
    });
    return bal;
  }, [items]);

  const pendingSum = useMemo(() => {
    let sum = 0;
    items.forEach((p) => {
      if (p.payment_status === "PAID") return;
      const amt = Number(p.amount || 0);
      if (p.type === "income") sum += amt;
      else sum -= amt;
    });
    return sum;
  }, [items]);

  const openCreate = () => {
    setEditingTx(null);
    setIsOpen(true);
  };

  const openEdit = (tx) => {
    setEditingTx(tx);
    setIsOpen(true);
  };

  const handleSaved = async () => {
    setIsOpen(false);
    setEditingTx(null);
    await load();
  };

  const handleDelete = async () => {
    if (!txToDelete) return;
    try {
      await deleteSupplierPrepayment(txToDelete.id);
      setTxToDelete(null);
      await load();
    } catch (err) {
      setError(err.message || "Не удалось удалить");
    }
  };

  return (
    <div className="max-w-6xl mx-auto mt-10 px-4">
      <button
        type="button"
        onClick={() => navigate("/accounting?tab=accounts&subtab=suppliers")}
        className="mb-4 text-sm text-slate-500 hover:text-blue-600"
      >
        ← Все поставщики
      </button>

      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="text-2xl font-bold">{supplierName}</h1>
          <p className="text-gray-600 mt-1">
            Баланс : ¥{" "}
            {balance.toLocaleString("ru-RU", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-sm text-amber-700">
            В ожидании : ¥{" "}
            {pendingSum.toLocaleString("ru-RU", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>

        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-green-600 text-white px-4 py-2 text-sm font-semibold hover:bg-green-700"
        >
          + Добавить платёж
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-6 text-center text-slate-500">Загрузка…</div>
      ) : (
        <div className="mt-6 border rounded-xl overflow-hidden">
          <div className="grid grid-cols-9 bg-gray-100 p-3 font-semibold text-sm">
            <div>Дата</div>
            <div>Тип</div>
            <div>Сумма</div>
            <div>Заказ</div>
            <div>Распред.</div>
            <div>Платёж через</div>
            <div>Статус</div>
            <div>Документы</div>
            <div className="text-right">Действия</div>
          </div>

          {sortedItems.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Движений пока нет
            </div>
          ) : (
            sortedItems.map((tx) => {
              const isIncome = tx.type === "income";
              const color = isIncome ? "text-green-600" : "text-red-600";
              const sign = isIncome ? "+" : "−";
              const docCount = tx.documents?.length || 0;
              const isPaid = tx.payment_status === "PAID";
              const recordId = tx.allocation
                ? allocationRecordMap[String(tx.allocation)]
                : null;
              const createdDay = formatCreatedDay(tx.created_at);

              return (
                <div
                  key={tx.id}
                  className="grid grid-cols-9 p-3 border-t items-center text-sm"
                >
                  <div>
                    <div>{formatDate(tx.date)}</div>
                    {createdDay && (
                      <div className="text-[11px] text-slate-400">
                        создано {createdDay}
                      </div>
                    )}
                  </div>

                  <div className={color}>
                    {isIncome ? "Приход" : "Расход"}
                  </div>

                  <div className={color}>
                    {sign}¥ {Number(tx.amount).toFixed(2)}
                  </div>

                  <div className="truncate">
                    {tx.order ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/orders/${tx.order}`);
                        }}
                        className="text-blue-700 hover:underline"
                      >
                        {tx.order_code || `#${tx.order}`}
                      </button>
                    ) : (
                      "-"
                    )}
                  </div>

                  <div className="truncate">
                    {tx.allocation && recordId ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/accounting/${recordId}/allocations/${tx.allocation}`
                          );
                        }}
                        className="text-blue-700 hover:underline"
                      >
                        #{tx.allocation}
                      </button>
                    ) : (
                      "-"
                    )}
                  </div>

                  <div className="truncate">{tx.payment_agent || "-"}</div>

                  <div>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                        isPaid
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {STATUS_LABELS[tx.payment_status] ||
                        tx.payment_status ||
                        "-"}
                    </span>
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => setDocsItem(tx)}
                      className={`text-xs underline ${
                        docCount > 0
                          ? "text-blue-700 hover:text-blue-900"
                          : "text-slate-400 hover:text-slate-600"
                      }`}
                    >
                      Документы ({docCount})
                    </button>
                  </div>

                  <div className="flex gap-1 justify-end">
                    <button
                      type="button"
                      onClick={() => openEdit(tx)}
                      className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      ✏
                    </button>
                    <button
                      type="button"
                      onClick={() => setTxToDelete(tx)}
                      className="rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })
          )}

          <div ref={endRef} />
        </div>
      )}

      <SupplierMovementFormModal
        open={isOpen}
        mode={editingTx ? "edit" : "create"}
        initial={editingTx}
        supplierId={Number(supplierId)}
        onClose={() => {
          setIsOpen(false);
          setEditingTx(null);
        }}
        onSaved={handleSaved}
      />

      <SupplierDocumentsModal
        open={Boolean(docsItem)}
        item={docsItem}
        onClose={() => setDocsItem(null)}
      />

      {txToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96">
            <h3 className="text-lg font-bold mb-4">Удалить платёж?</h3>
            <p className="mb-6 text-gray-600">
              {txToDelete.type === "income" ? "Приход" : "Расход"} · ¥
              {Number(txToDelete.amount).toFixed(2)}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded"
                onClick={() => setTxToDelete(null)}
              >
                Отмена
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={handleDelete}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}